import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { format } from "date-fns";
import { useToast } from "../components/ui/use-toast";
import { getThought, updateThought, deleteThought } from "../services/supabase/thoughts";
import { EditableField } from "../components/action-item/EditableField";
import { HeaderControls } from "../components/shared/HeaderControls";
import React from "react";
import { Button } from "../components/ui/button";

export function ThoughtDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: thought, isLoading } = useQuery({
    queryKey: ["thought", id],
    queryFn: () => {
      if (!id) throw new Error("No thought ID provided");
      return getThought(id);
    }
  });

  const updateThoughtMutation = useMutation({
    mutationFn: async ({ content }: { content: string }) => {
      if (!id) throw new Error("No thought ID provided");
      return updateThought(id, { content });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["thought", id] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: "Failed to update thought: " + error.message,
        variant: "destructive",
      });
    },
  });

  const deleteThoughtMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("No thought ID provided");
      return deleteThought(id);
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Thought deleted successfully",
      });
      navigate("/app/thoughts");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: "Failed to delete thought: " + error.message,
        variant: "destructive",
      });
    },
  });

  const handleContentChange = async (content: string) => {
    await updateThoughtMutation.mutate({ content });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">Loading thought...</p>
      </div>
    );
  }

  if (!thought) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">Thought not found</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <HeaderControls 
        onDelete={() => deleteThoughtMutation.mutate()}
        className="justify-between md:justify-end"
      >
        <Button onClick={() => deleteThoughtMutation.mutate()} className="button button-destructive">
          Delete Thought
        </Button>
      </HeaderControls>

      <Card>
        <CardContent className="space-y-4 pt-4">
          <div className="text-sm text-gray-500">
            {format(new Date(thought.created_at || ''), "PPP")}
          </div>
          
          <EditableField
            value={thought.content || ''}
            onChange={handleContentChange}
            type="textarea"
            placeholder="Write your thought here..."
            showToast={true}
          />

          {thought.audio_url && (
            <div className="mt-4">
              <h3 className="text-sm font-medium mb-2">Audio Recording</h3>
              <audio controls className="w-full">
                <source src={thought.audio_url} type="audio/webm" />
                Your browser does not support the audio element.
              </audio>
            </div>
          )}

          {thought.tags && (
            <div className="flex gap-2 flex-wrap">
              {JSON.parse(thought.tags).map((tag: { id: string; name: string }) => (
                <span
                  key={tag.id}
                  className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary"
                >
                  {tag.name}
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
